import React, { useState } from "react";
import { Navigate, useNavigate, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { api, formatApiError } from "../lib/api";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import BrandMark from "../components/BrandMark";
import PasswordInput from "../components/PasswordInput";
import { toast } from "sonner";
import { ArrowLeft, KeyRound } from "lucide-react";

/**
 * ChangePassword — self-service password rotation for already-authenticated
 * Owners. Reuses `POST /api/auth/rotate-password` (which enforces strong-
 * password rules server-side + invalidates all other sessions). Distinct
 * from `RotatePassword.jsx` which is the forced first-login rotation
 * (banner + gate on `must_rotate_password:true`). On success we sign the
 * user out and route them to /login so they must re-enter the new value —
 * this is the standard security posture for a password change.
 */

const rules = [
  { text: "Minimum 12 characters", check: (p) => p.length >= 12 },
  { text: "At least 1 uppercase letter", check: (p) => /[A-Z]/.test(p) },
  { text: "At least 1 lowercase letter", check: (p) => /[a-z]/.test(p) },
  { text: "At least 1 number", check: (p) => /\d/.test(p) },
  { text: "At least 1 special character", check: (p) => /[^A-Za-z0-9]/.test(p) },
];

export default function ChangePassword() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [cur, setCur] = useState("");
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [errCode, setErrCode] = useState("");
  const [visibilityResetKey, setVisibilityResetKey] = useState(0);

  if (user === false || user === null) return <Navigate to="/login" replace />;
  // If the user is on the forced-rotation path, keep them on the forced page.
  if (user && user.must_rotate_password) return <Navigate to="/auth/rotate" replace />;

  const submit = async (e) => {
    e.preventDefault();
    setErr(""); setErrCode("");
    const cleanCur = (cur || "").trim();
    const cleanPw = (pw || "").trim();
    const cleanPw2 = (pw2 || "").trim();
    if (cleanPw !== cleanPw2) return setErr("New password and confirmation don't match.");
    if (!rules.every((r) => r.check(cleanPw))) return setErr("New password does not meet all requirements below.");
    if (cleanPw === cleanCur) return setErr("New password must be different from your current password.");
    setBusy(true);
    try {
      await api.post("/auth/rotate-password", { current_password: cleanCur, new_password: cleanPw });
      toast.success("Password updated. Please sign in with your new password.");
      // Rotate-password already invalidates all OTHER sessions and issues a fresh
      // access token for THIS session. For a clean UX we still log the user out
      // so they explicitly re-authenticate with the new password.
      await logout();
      navigate("/login", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      const code = typeof detail === "object" ? detail?.code : null;
      const message = typeof detail === "object" ? detail?.message : detail;
      setErrCode(code || (status === 401 ? "current_password_incorrect" : ""));
      setErr(message || formatApiError(detail) || e.message || "Password change failed.");
    } finally {
      setBusy(false);
      setVisibilityResetKey((k) => k + 1);
    }
  };

  return (
    <div className="min-h-screen bg-erf-cream flex items-center justify-center p-6" data-testid="change-password-page">
      <div className="w-full max-w-md bg-white border-t-4 border-t-erf-gold border border-erf-borderCream rounded-lg shadow-lg p-6 space-y-5">
        <button
          type="button"
          onClick={() => navigate(-1)}
          className="flex items-center gap-1 text-xs font-nav text-erf-dark/60 hover:text-erf-dark"
          data-testid="change-password-back"
        >
          <ArrowLeft className="w-3 h-3" /> Back
        </button>

        <div className="flex items-center gap-3">
          <BrandMark size={48} />
          <div>
            <div className="text-erf-gold font-mono text-[10px] uppercase tracking-widest">Account security</div>
            <h1 className="font-display font-bold text-xl text-erf-dark flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-erf-gold" /> Change password
            </h1>
            <div className="text-xs font-nav text-erf-dark/60 mt-0.5">Signed in as <span className="font-mono">{user?.email}</span></div>
          </div>
        </div>

        <form onSubmit={submit} className="space-y-4" data-testid="change-password-form">
          <div>
            <Label htmlFor="cp-current" className="text-xs uppercase font-nav tracking-wider text-erf-dark/70">Current password</Label>
            <PasswordInput id="cp-current" value={cur} onChange={(e) => setCur(e.target.value)} required className="font-mono mt-1" testId="change-current-input" resetVisibilityKey={visibilityResetKey} autoComplete="current-password" />
          </div>
          <div>
            <Label htmlFor="cp-new" className="text-xs uppercase font-nav tracking-wider text-erf-dark/70">New password</Label>
            <PasswordInput id="cp-new" value={pw} onChange={(e) => setPw(e.target.value)} required className="font-mono mt-1" testId="change-new-input" resetVisibilityKey={visibilityResetKey} autoComplete="new-password" />
          </div>
          <div>
            <Label htmlFor="cp-confirm" className="text-xs uppercase font-nav tracking-wider text-erf-dark/70">Confirm new password</Label>
            <PasswordInput id="cp-confirm" value={pw2} onChange={(e) => setPw2(e.target.value)} required className="font-mono mt-1" testId="change-confirm-input" resetVisibilityKey={visibilityResetKey} autoComplete="new-password" />
          </div>

          <ul className="text-xs space-y-1 font-nav" data-testid="change-rules">
            {rules.map((r) => {
              const ok = r.check(pw);
              return (
                <li key={r.text} className={ok ? "text-green-700" : "text-erf-dark/60"}>
                  {ok ? "✓" : "•"} {r.text}
                </li>
              );
            })}
          </ul>

          {err && (
            <div
              className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-sm px-3 py-2 font-nav"
              data-testid="change-error"
              data-error-code={errCode || undefined}
            >
              {err}
            </div>
          )}

          <Button
            type="submit"
            disabled={busy}
            className="w-full h-10 bg-erf-gold hover:bg-erf-goldHover text-erf-dark font-nav uppercase tracking-widest rounded-sm"
            data-testid="change-submit-btn"
          >
            {busy ? "Updating…" : "Update password"}
          </Button>

          <div className="text-[11px] font-mono text-erf-dark/50 text-center pt-2">
            After a successful change you'll be signed out and asked to sign in again with the new password. All other active sessions will be invalidated.
          </div>
        </form>
      </div>
    </div>
  );
}
